import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useSetAtom } from 'jotai';

import { showToast } from '@restworld/ui-ds-base';
import { useAccountsService } from '@restworld/data-services';
import { employerUserContactQueryKey, useEmployerUserContactData } from '@restworld/feature-authentication';
import { EmployerUserContact } from '@restworld/utility-types';

import {
  modalManageNotificationsVisibleAtom,
  modalCommunicationPrefVisibleAtom,
  modalUserInformationVisibleAtom,
} from '.';
import { emailSentAtom, emailSentDateAtom, emailSentFlagAtom } from './update-user-email-modal';
import {
  phoneNumberSentAtom,
  phoneNumberSentFlagAtom,
  phoneNumberVerificationModalVisibleAtom,
  phoneNumberVerificationDateNowAtom,
} from './update-phone-number-modal';
import { validateOtpInputVisibleAtom } from './otp-validation-modal';
import { useV2Router } from '../dashboard/hooks/use-v2-router';

// --- GESTIONE NOTIFICHE ---
export const useEmployerManageNotificationsMutation = () => {
  const queryClient = useQueryClient();
  const accountsService = useAccountsService();
  const employerUserInformation = useEmployerUserContactData();
  const queryKey = employerUserContactQueryKey;
  const modalManageNotificationsVisibleToggler = useSetAtom(modalManageNotificationsVisibleAtom);

  const { mutate, isPending } = useMutation({
    mutationFn: (params: {
      user_id: string;
      weekly_spontaneous_applications_email: boolean;
      showcase_monthly_performance_email?: boolean;
      daily_recommended_candidates_message?: boolean;
    }) => accountsService.updateEmployerUserContact(params, params.user_id),
    onMutate(userData) {
      queryClient.cancelQueries({ queryKey: employerUserContactQueryKey });
      if (employerUserInformation) {
        const newData = {
          ...employerUserInformation,
          ...userData,
        };
        queryClient.setQueryData(queryKey, newData);
      }
      return { previousData: employerUserInformation };
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey });
      modalManageNotificationsVisibleToggler(false);
    },
    onError(error, _, { previousData }) {
      // eslint-disable-next-line no-console
      console.error(`Error updating employer user contact: manage notifications.`, error);
      queryClient.setQueryData(queryKey, previousData);
    },
  });
  return { mutate, isPending };
};

export const useEmployerUserInformationMutation = () => {
  const modalUserInformationVisibleToggler = useSetAtom(modalUserInformationVisibleAtom);
  const queryClient = useQueryClient();
  const accountsService = useAccountsService();
  const employerUserInformation = useEmployerUserContactData();
  const queryKey = employerUserContactQueryKey;

  const { mutate, isPending } = useMutation({
    mutationFn: (params: { name?: string; surname?: string; picture?: File | null; id?: string }) => {
      if (params['picture'] instanceof File) {
        const formData = new FormData();
        formData.append('picture', params['picture'] as File);
        return accountsService.updateEmployerUserContact(formData, params.id);
      } else {
        return accountsService.updateEmployerUserContact(
          {
            name: params.name,
            surname: params.surname,
          },
          params.id
        );
      }
    },
    onMutate(params) {
      if (employerUserInformation && params['name']) {
        queryClient.cancelQueries({ queryKey });
        const newData = {
          ...employerUserInformation,
          user: {
            ...{
              ...employerUserInformation.user,
              ...params,
            },
          },
        };
        queryClient.setQueryData(queryKey, newData);
        return {
          newData,
        };
      }
      return { previousData: employerUserInformation };
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey });
      modalUserInformationVisibleToggler(false);
    },
    onError(error, _, { previousData }) {
      console.error(`Error updating employer user contact`, error);
      queryClient.setQueryData(queryKey, previousData);
    },
  });
  return { mutate, isPending };
};

export const useEmployerCommunicationPrefMutation = () => {
  const queryClient = useQueryClient();
  const accountsService = useAccountsService();
  const employerUserInformation = useEmployerUserContactData();
  const queryKey = employerUserContactQueryKey;
  const modalCommunicationPrefVisibleToggler = useSetAtom(modalCommunicationPrefVisibleAtom);

  const { mutate, isPending } = useMutation({
    mutationFn: (params: EmployerUserContact) => accountsService.updateEmployerUserContact(params, params.user_id),
    onMutate(userData) {
      queryClient.cancelQueries({ queryKey: employerUserContactQueryKey });
      if (employerUserInformation) {
        const newData = {
          ...employerUserInformation,
          ...userData,
        };
        queryClient.setQueryData(queryKey, newData);
      }
      return { previousData: employerUserInformation };
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey });
      modalCommunicationPrefVisibleToggler(false);
    },
    onError(error, _, { previousData }) {
      console.error(`Error updating employer user contact: communication pref.`, error);
      queryClient.setQueryData(queryKey, previousData);
    },
  });
  return { mutate, isPending };
};
export const useEmployerEmailUpdateMutation = () => {
  const acc = useAccountsService();
  const setEmailSentFlag = useSetAtom(emailSentFlagAtom);
  const setEmailSent = useSetAtom(emailSentAtom);
  const setEmailSentDate = useSetAtom(emailSentDateAtom);
  const { mutate, isPending, error, isError, data, isSuccess, reset } = useMutation({
    mutationKey: ['update-email'],
    mutationFn: (params: { email?: string; phoneNumber?: string }) => acc.updateAuthenticationDetails(params),
    onMutate(variables) {
      /* */
    },
    onSuccess(data, variables, context) {
      setEmailSentFlag(true);
      setEmailSent(variables.email);
      setEmailSentDate(Date.now());
      return data;
    },
    onError(error, variables, context) {
      return error;
    },
  });
  return {
    mutate,
    isPending,
    isError,
    error,
    data,
    isSuccess,
    reset,
  };
};
export const emailChangeVerifyMutationTokenKey = ['email-verification-from-token'];
export const useVerifyEmailFromURLTokenMutation = () => {
  const router = useV2Router();
  let urlParams: URLSearchParams | null = null;
  if (typeof window !== 'undefined') {
    urlParams = new URLSearchParams(window.location.search);
  }
  const token = urlParams?.get('token');
  const queryClient = useQueryClient();
  const acc = useAccountsService();
  const setEmailSentFlag = useSetAtom(emailSentFlagAtom);
  const setEmailSent = useSetAtom(emailSentAtom);
  const setEmailSentDate = useSetAtom(emailSentDateAtom);

  const { t } = useTranslation('user-settings');

  const { mutate: verifyEmailFromURLToken, isPending } = useMutation({
    mutationFn: () => acc.verifyEmail({ token }),
    onMutate(variables) {
      queryClient.cancelQueries({ queryKey: employerUserContactQueryKey });
    },
    onSuccess(data) {
      router.push('/employer/account', null, {
        shallow: true,
      });
      setEmailSentFlag(false);
      setEmailSent('');
      setEmailSentDate(null);

      showToast(t('email_verified'), 'success', {
        position: 'top-right',
        style: {
          marginTop: 80,
        },
        autoClose: 10000,
      });
      queryClient.invalidateQueries({ queryKey: employerUserContactQueryKey });
      return data;
    },
    onError(error) {
      router.push('/employer/account', null, {
        shallow: true,
      });
      showToast(error?.['data']?.error, 'error', {
        position: 'top-right',
        style: {
          marginTop: 80,
        },
        autoClose: 10000,
      });
      return error;
    },
    mutationKey: emailChangeVerifyMutationTokenKey,
  });

  useEffect(() => {
    if (token) verifyEmailFromURLToken();
  }, [token, verifyEmailFromURLToken]);

  return {
    isPending,
  };
};
export const useEmployerPhoneNumberUpdateMutation = () => {
  const acc = useAccountsService();
  const setPhoneNumberSentFlag = useSetAtom(phoneNumberSentFlagAtom);
  const setPhoneNumberSent = useSetAtom(phoneNumberSentAtom);
  const validateOtpInputToggler = useSetAtom(validateOtpInputVisibleAtom);
  const setPhoneNumberVerificationDateNow = useSetAtom(phoneNumberVerificationDateNowAtom);
  const phoneNumberVerificationModalVisibleToggler = useSetAtom(phoneNumberVerificationModalVisibleAtom);

  const mutation = useMutation({
    mutationKey: ['update-phone_number'],
    mutationFn: (params: { email?: string; phoneNumber?: string }) => acc.updateAuthenticationDetails(params),
    onMutate(variables) {
      /* */
    },
    onSuccess(data, variables, context) {
      setPhoneNumberSentFlag(true);
      setPhoneNumberSent(variables.phoneNumber);
      validateOtpInputToggler(true);
      phoneNumberVerificationModalVisibleToggler(false);
      setPhoneNumberVerificationDateNow(Date.now());
      return data;
    },
    onError(error, variables, context) {
      return error;
    },
  });
  return mutation;
};
export const useOTPVerificationMutation = () => {
  const queryClient = useQueryClient();
  const acc = useAccountsService();
  const setPhoneNumberSentFlag = useSetAtom(phoneNumberSentFlagAtom);
  const setPhoneNumberSent = useSetAtom(phoneNumberSentAtom);
  const validateOtpInputToggler = useSetAtom(validateOtpInputVisibleAtom);
  const { t } = useTranslation('user-settings');
  const setPhoneNumberVerificationDateNow = useSetAtom(phoneNumberVerificationDateNowAtom);

  const mutation = useMutation({
    mutationKey: ['phone_number-otp-verification'],
    mutationFn: (params: { otpCode: string }) => acc.verifyPhoneOtp(params),
    onMutate(variables) {
      queryClient.cancelQueries({ queryKey: employerUserContactQueryKey });
    },
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({ queryKey: employerUserContactQueryKey });
      setPhoneNumberSentFlag(false);
      setPhoneNumberSent('');
      setPhoneNumberVerificationDateNow(null);
      validateOtpInputToggler(false);
      showToast(t('phone_number_verified'), 'success', {
        position: 'top-right',
        style: {
          marginTop: 80,
        },
        autoClose: 10000,
      });
      return data;
    },
    onError(error, variables, context) {
      return error;
    },
  });
  return mutation;
};
